import OwnerModel                 from '@models/map/OwnerModel';
import ProjectStatusModel         from '@models/map/ProjectStatusModel';
import doc                        from '@widesk-core/decorators/doc';
import model                      from '@widesk-core/decorators/model';
import MapServiceModel            from '@/models/MapServiceModel';
import TimestampAble              from '@widesk-core/models/traits/TimestampAble';
import Blamable                   from '@/models/traits/Blamable';
import { computed }               from 'mobx';
import reverseResolvable          from '@widesk-core/decorators/reverseResolvable';
import StonecodeFileFileModel     from '../file/StonecodeFileFileModel';
import { RenderNameProps }        from '@widesk-ui/mixins/ApiModelRenderName';
import FileHelper                 from '@/helpers/FileHelper';
import ProjectRegenerateSlugModel from '@models/map/ProjectRegenerateSlugModel';
import ThemeModel                 from '@models/map/ThemeModel';

/**
 * Configuration d'un projet. Mêmes clés que celle d'une carte, et ouverte : l'API accepte les clés
 * supplémentaires, c'est là que vivent les réglages qui étaient des `ProjectMeta`.
 */
export type ProjectConfiguration = {
	placeTypeFiltrable?: boolean;
	[key: string]: unknown;
};

@model.urnResource('project')
@doc.path('/projects/{?id}')
export default class ProjectModel extends Blamable(TimestampAble(MapServiceModel)) {
	private declare _tsFilters: {
		'createdAt': string;
		'maps': id;
		'owner': id;
		'owner.accountUrn': Urn;
		'placeTypes': id;
		'places': id;
		'projectStatus': id;
		'projectStatusHistories': id;
		'slug': string;
		'updatedAt': string | Date | Dayjs;
	};

	private declare _tsSorts: {
		'createdAt': string;
		'id': string;
		'projectStatus': string;
		'title': string;
		'updatedAt': string;
	};

	@doc.model(OwnerModel) declare owner: OwnerModel;
	/**
	 * Thème appliqué d'office aux formes nouvellement dessinées. Porté par le projet et non par un
	 * drapeau sur chaque thème : l'API n'en accepte qu'un, il ne peut donc pas y en avoir deux, et
	 * l'absence de valeur est un état légitime (aucun thème par défaut).
	 */
	@doc.model(ThemeModel) declare defaultTheme: ThemeModel;
	@doc.model(ProjectStatusModel) declare projectStatus: ProjectStatusModel;
	/** Adresse publique du projet : `GET /projects/slug/{slug}`. Générée à la création. */
	@doc.string declare slug: string;
	@doc.string declare title: string;

	/**
	 * Configuration du projet, validée par le même schéma que celle d'une carte (`minZoom`,
	 * `maxZoom`, `initialZoom`, `displayStreets`, `bounds`…, les clés supplémentaires sont acceptées).
	 * Elle est figée dans le fichier condensé au moment de la publication : ce qu'un visiteur voit
	 * est celle de la dernière publication, pas celle-ci.
	 */
	@doc.json declare configuration: ProjectConfiguration;

	/**
	 * Redonne au projet une adresse publique neuve — les liens déjà diffusés cessent aussitôt de
	 * fonctionner. Le nouveau slug est reporté sur ce modèle pour que les écrans ouverts l'affichent
	 * sans recharger le projet.
	 */
	public async regenerateSlug() {
		const action = new ProjectRegenerateSlugModel({ id: this.id });

		await action.patch({});

		this.set('slug', action.get('slug'));

		return this.slug;
	}

	@computed
	public get imageFile() {
		return reverseResolvable(this, StonecodeFileFileModel, {
			propertyName: 'imageFile',
			modelIdProperty: 'urn',
			getModelIdFromResolvable: async file => {
				await file.typeSource.source.whenIsLoaded();
				return file.typeSource.source.entityUrn;
			},
			filters: ({ urns }) => ({
				'typeSource.type.reference': 'project_image' as const,
				'typeSource.source.entityUrn': urns,
				'fileStatus.reference': 'available' as const,
			}),
			onFetchSuccess: async m => {
				if (m.imageFile.id) {
					const src = await FileHelper.fetchSrcForFile(m.imageFile);
					m.set('imageSrc', src);
				}
			},
		});
	}

	public get imageSrc(): string {
		return this.get('imageSrc', '');
	}

	public renderName(props?: RenderNameProps<this> & RenderNameImageProps) {
		return super.renderName(props);
	}
}